export const getInitials = (name) =>
  name
    ? ((words) => (words[0]?.[0] || '') + (words.length > 1 ? words[words.length - 1][0] : ''))(
        name.trim().split(/\s+/)
      ).toUpperCase()
    : '';

const COLORS = [
  '#F87171',
  '#FBBF24',
  '#34D399',
  '#60A5FA',
  '#A78BFA',
  '#F472B6',
  '#FCD34D',
  '#38BDF8',
  '#818CF8'
];

const hexToHsl = (hex) => {
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }

  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    // eslint-disable-next-line default-case
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }

  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
};

export const getColorPairFromInitials = (initials) => {
  if (!initials) return { light: '#eee', dark: '#333' };

  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = initials.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % COLORS.length;
  const baseHex = COLORS[index];
  const { h, s } = hexToHsl(baseHex);

  return {
    light: `hsl(${h}, ${s}%, 85%)`,
    dark: `hsl(${h}, ${s}%, 35%)`
  };
};

export const getPageNumbers = (table, maxPagesToShow = 5) => {
  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;

  if (pageCount <= maxPagesToShow) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const pages = [];
  const sideCount = Math.floor((maxPagesToShow - 3) / 2);

  let start = Math.max(2, currentPage - sideCount);
  let end = Math.min(pageCount - 1, currentPage + sideCount);

  if (currentPage <= sideCount + 2) {
    start = 2;
    end = maxPagesToShow - 2;
  }

  if (currentPage >= pageCount - sideCount - 1) {
    start = pageCount - (maxPagesToShow - 3);
    end = pageCount - 1;
  }

  pages.push(1);

  if (start > 2) {
    pages.push('...');
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (end < pageCount - 1) {
    pages.push('...');
  }

  pages.push(pageCount);

  return pages;
};

export const exportTableToCSV = (table, exportFields) => {
  if (!table) return;

  const rows = table.getFilteredRowModel().rows;

  if (rows.length === 0 || exportFields.length === 0) {
    alert('No rows or fields selected.');
    return;
  }

  // Detect system delimiter (comma or semicolon)
  const getSystemDelimiter = () => {
    const formatted = Intl.NumberFormat().format(1.1);
    return formatted.includes(',') ? ';' : ',';
  };

  const delimiter = getSystemDelimiter();

  const headers = exportFields.map((field) => table.getColumn(field)?.columnDef.header ?? field);

  const csvRows = [
    headers.join(delimiter),
    ...rows.map((row) =>
      exportFields
        .map((field) => {
          const value = row.getValue(field);
          if (typeof value === 'string') {
            return `"${value.replace(/"/g, '""')}"`; // Escape quotes
          }
          return value;
        })
        .join(delimiter)
    )
  ];

  const csvContent = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', 'export.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
